import React, { useState, useRef, useEffect } from "react";
import ArtworkAlert from "../ArtworkAlert.vue";
import { useDtfStore } from "../store";
import { cn } from "tailwind-variants";
import "./Sizes.scss";
import { renderList } from "common/vue/render-list";

export default function Sizes({ ...restProps }) {
  const heights = [
    { label: "2 Inches", value: 2 },
    { label: "2.5 Inches", value: 2.5 },
    { label: "3 Inches", value: 3 }
  ];
  const heightValues = heights.map(height => height.value);

  const maxLengths = [
    { label: "9 Inches", value: 9 },
    { label: "11 Inches", value: 11 },
    { label: "14 Inches", value: 14 }
  ];
  const maxLengthValues = maxLengths.map(maxLength => maxLength.value);

  const timerRef = useRef(null);
  const [letterHeight, setLetterHeight] = useState(null);
  const [letterMaxLength, setLetterMaxLength] = useState(null);
  const prevHeightRef = useRef(null);
  const prevMaxLengthRef = useRef(null);

  const store = useDtfStore();

  function letterNumberArtwork() {
    return store.letterNumberArtwork;
  }

  function isHeightSelected(option) {
    return letterNumberArtwork().height === option.value && !letterNumberArtwork().showCustomHeight;
  }

  function isMaxLengthSelected(option) {
    return (
      letterNumberArtwork().maxLength === option.value && !letterNumberArtwork().showCustomMaxLength
    );
  }

  function showCustomHeight() {
    return letterNumberArtwork().showCustomHeight;
  }

  function showCustomMaxLength() {
    return letterNumberArtwork().showCustomMaxLength;
  }

  function dtfMinHeight() {
    return store.dtfMinHeight;
  }

  function dtfMaxHeight() {
    return store.dtfMaxHeight;
  }

  function dtfMinWidth() {
    return store.dtfMinWidth;
  }

  function dtfMaxWidth() {
    return store.dtfMaxWidth;
  }

  function isValidLetterHeight() {
    return letterHeight >= dtfMinHeight() && letterHeight <= dtfMaxHeight();
  }

  function isValidLetterMaxLength() {
    return letterMaxLength >= dtfMinWidth() && letterMaxLength <= dtfMaxWidth();
  }

  function isValidDimension() {
    return isValidLetterMaxLength() && isValidLetterHeight();
  }

  function updateHeight(option) {
    let height = option.value;
    const isValid = isValidDimension();
    if (height > dtfMaxHeight()) height = dtfMaxHeight();
    store.updateArtworkValues({
      index: 0,
      isValid,
      height
    });
    if (isValid) store.updateAllLetterPreviews();
  }

  function updateMaxLength(option) {
    let maxLength = option.value;
    const isValid = isValidDimension();
    if (maxLength > dtfMaxWidth()) maxLength = dtfMaxWidth();
    store.updateArtworkValues({
      index: 0,
      isValid,
      maxLength
    });
    if (isValid) store.updateAllLetterPreviews();
  }

  function handleHeightSet(option) {
    setLetterHeight(option.value);
    store.updateArtworkValues({
      index: 0,
      showCustomHeight: false
    });
    updateHeight(option);
  }

  function handleMaxLengthSet(option) {
    setLetterMaxLength(option.value);
    store.updateArtworkValues({
      index: 0,
      showCustomMaxLength: false
    });
    updateMaxLength(option);
  }

  function handleCustomHeight() {
    store.updateArtworkValues({
      index: 0,
      isValid: false,
      showCustomHeight: true,
      height: null
    });
  }

  function handleCustomMaxLength() {
    store.updateArtworkValues({
      index: 0,
      isValid: false,
      showCustomMaxLength: true,
      maxLength: null
    });
  }

  function handleCustomInputChange(event, callback) {
    clearTimeout(timerRef.current);
    const { value } = event.target;
    let inputValue = value.match(/(\d+(\.\d*)?)/);
    inputValue = inputValue ? inputValue[0] : "0";
    const splitHeight = inputValue.split(".");
    if (splitHeight[1] && splitHeight[1].length > 2) {
      inputValue = Number([splitHeight[0], splitHeight[1].slice(0, 2)].join("."));
    }
    timerRef.current = setTimeout(() => {
      callback({ value: Number(inputValue) });
    }, 1000);
  }

  useEffect(() => {
    const currentHeight = letterNumberArtwork().height;
    if (prevHeightRef.current && currentHeight === null) {
      setLetterHeight(null);
    }
    prevHeightRef.current = currentHeight;
  }, [store.letterNumberArtwork.height]);

  useEffect(() => {
    const currentMaxLength = letterNumberArtwork().maxLength;
    if (prevMaxLengthRef.current && currentMaxLength === null) {
      setLetterMaxLength(null);
    }
    prevMaxLengthRef.current = currentMaxLength;
  }, [store.letterNumberArtwork.maxLength]);

  useEffect(() => {
    const { height, maxLength } = letterNumberArtwork();
    if (!height || !maxLength) {
      return;
    }
    const isValid = isValidLetterHeight() && isValidLetterMaxLength();
    let showCustomHeightInput = false;
    let showCustomMaxLengthInput = false;
    if (!heightValues.includes(height)) showCustomHeightInput = true;
    if (!maxLengthValues.includes(maxLength)) showCustomMaxLengthInput = true;
    store.updateArtworkValues({
      index: 0,
      isValid,
      showCustomHeight: showCustomHeightInput,
      showCustomMaxLength: showCustomMaxLengthInput
    });
  }, []);

  return (
    <div {...restProps} className={cn("sizes", restProps.className)}>
      <h3>Letter Height</h3>
      <div className="sizes__height">
        {renderList(heights, height => (
          <div
            className={cn({
              sizes__size: true,
              "sizes__size--selected": isHeightSelected(height)
            })}
            key={height.value}
            onClick={() => {
              handleHeightSet(height);
            }}
            data-testid="sizes-div"
          >
            {height.label}
          </div>
        ))}
        <div
          className={cn({
            sizes__size: true,
            "sizes__size--selected": showCustomHeight()
          })}
          onClick={() => {
            handleCustomHeight();
          }}
          data-testid="sizes-div-1"
        >
          {" "}
          Custom{" "}
        </div>
      </div>
      {showCustomHeight() ? (
        <>
          <p className="sizes__label">Height (inches)</p>
          <input
            className="sizes__input"
            value={letterHeight ?? ""}
            onInput={e => {
              setLetterHeight(e.target.value);
              handleCustomInputChange(e, updateHeight);
            }}
            maxLength="6"
            type="number"
            data-testid="sizes-input"
          />
          {letterHeight && !isValidLetterHeight() ? (
            <ArtworkAlert className="sizes__error" data-testid="sizes-error">
              {"Please enter a valid height between"}
              {dtfMinHeight()}
              {"and"}
              {dtfMaxHeight()}
              {"inches"}
            </ArtworkAlert>
          ) : null}
        </>
      ) : null}
      <h3>Maximum Length</h3>
      <div className="sizes__max-lengths">
        {renderList(maxLengths, maxLength => (
          <div
            className={cn({
              sizes__size: true,
              "sizes__size--selected": isMaxLengthSelected(maxLength)
            })}
            key={maxLength.value}
            onClick={() => {
              handleMaxLengthSet(maxLength);
            }}
            data-testid="sizes-div-2"
          >
            {maxLength.label}
          </div>
        ))}
        <div
          className={cn({
            sizes__size: true,
            "sizes__size--selected": showCustomMaxLength()
          })}
          onClick={() => {
            handleCustomMaxLength();
          }}
          data-testid="sizes-div-3"
        >
          {" "}
          Custom{" "}
        </div>
      </div>
      {showCustomMaxLength() ? (
        <>
          <p className="sizes__label">Length (inches)</p>
          <input
            className="sizes__input"
            value={letterMaxLength ?? ""}
            onInput={e => {
              setLetterMaxLength(e.target.value);
              handleCustomInputChange(e, updateMaxLength);
            }}
            maxLength="6"
            type="number"
            data-testid="sizes-input-1"
          />
          {letterMaxLength && !isValidLetterMaxLength() ? (
            <ArtworkAlert className="sizes__error" data-testid="sizes-error-1">
              {"Please enter a valid max length between"}
              {dtfMinWidth()}
              {"and"}
              {dtfMaxWidth()}
              {"inches"}
            </ArtworkAlert>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
